import constants from 'common/constants'

import {
  DATA_UPLOAD_CONVERT,
  DATA_UPLOAD_DELETE_DRAFT,
  DATA_UPLOAD_GET_CHUNKED_STATUS,
  DATA_UPLOAD_GET_DRAFT,
  DATA_UPLOAD_GET_DRAFT_JOB,
  DATA_UPLOAD_GET_DRAFT_TEMPLATE,
  DATA_UPLOAD_GET_SUBJECT_ALIASES,
  DATA_UPLOAD_GET_USE_CASES,
  DATA_UPLOAD_LIST_DRAFTS,
  DATA_UPLOAD_PUT_SUBJECT_ALIASES,
  DATA_UPLOAD_SEND_CHUNK,
  DATA_UPLOAD_START_CHUNKED,
} from './ActionTypes/ApiActionTypes'

/**
 * `GET data-upload/use-cases`: the upload limits (`result.limits`) and each use case's
 * `acceptedFormats`. AI servers only; elsewhere it answers 404 and callers keep their defaults
 * (see `DEFAULT_DATA_UPLOAD_CONFIG` in common/helpers/dataUpload).
 */
export const getDataUploadUseCases = (onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_GET_USE_CASES,
    payload: {
      apiUrl: constants.api_data_upload_use_cases,
      getString: '',
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `POST data-upload/convert`: any file(s) in, our own template workbook out. `body` is a
 * FormData built by `buildDataUploadConvertForm`. A photo or scan takes 30-50 s per page, so
 * callers must show a progress state; the saga's fetch has no client-side timeout. Failures
 * carry their code in `result.code` (see `dataUploadErrorCode`).
 */
export const convertDataUpload = (body, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_CONVERT,
    payload: {
      apiUrl: constants.api_data_upload_convert,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'multipart/form-data',
      reduxActionType: '',
      body,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `POST data-upload/uploads`: opens a chunked upload. nginx refuses any body over 1 MB, so
 * every file travels in pieces (see common/helpers/chunkedUpload). With `useCaseKey` a type the
 * screen can't read is refused here, before a byte is sent.
 *
 * @param {{fileName: string; size: number; contentType?: string; sha256?: string; useCaseKey?: string}} body
 */
export const startChunkedDataUpload = (body, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_START_CHUNKED,
    payload: {
      apiUrl: constants.api_data_upload_uploads,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      reduxActionType: '',
      body,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `POST data-upload/uploads/:uploadId/chunks/:index`: one piece, as the `chunk` field of a
 * FormData. Sending an index again replaces it, so a failed piece may be retried freely.
 */
export const sendDataUploadChunk = (uploadId, index, body, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_SEND_CHUNK,
    payload: {
      apiUrl: constants.api_data_upload_chunk(uploadId, index),
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'multipart/form-data',
      reduxActionType: '',
      body,
      onSuccess,
      onFailure,
    },
  }
}

/** `GET data-upload/uploads/:uploadId`: `{received: number[], chunkCount, complete}`, to resume. */
export const getChunkedDataUploadStatus = (uploadId, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_GET_CHUNKED_STATUS,
    payload: {
      apiUrl: constants.api_data_upload_upload(uploadId),
      getString: '',
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `GET data-upload/drafts?useCaseKey=…&title=…`: the user's drafts, newest first
 * (`result.drafts`).
 *
 * @param {{useCaseKey: string; title?: string}} params
 */
export const listDataUploadDrafts = (params, onSuccess, onFailure) => {
  const query = new URLSearchParams({useCaseKey: params.useCaseKey})
  if (params.title) query.set('title', params.title)
  return {
    type: DATA_UPLOAD_LIST_DRAFTS,
    payload: {
      apiUrl: constants.api_data_upload_drafts,
      getString: `?${query.toString()}`,
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/** `GET data-upload/drafts/:id/job`: `result.job.status` (queued / processing / done / failed…). */
export const getDataUploadDraftJob = (draftId, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_GET_DRAFT_JOB,
    payload: {
      apiUrl: constants.api_data_upload_draft_job(draftId),
      getString: '',
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `GET data-upload/drafts/:id/template?format=json&…`: the draft as our template
 * (`result.template.base64`) plus its `report`. Re-checked against the school at this moment,
 * so fetch it when the step opens. 409 JOB_RUNNING while the job is still reading.
 *
 * `branchAssignments` answers the template's own `branchQuestions` (Allocation prefill:
 * `{"<file>::<sheet>": <branchId> | "mixed"}`), sent as URL-encoded JSON. `allocationAssignments`
 * answers its `allocationQuestions` (`[{teacher, employeeId, subject, classIds}]`), the same way.
 *
 * @param {{flavour?: string; curriculumColumn?: boolean; branchAssignments?: Record<string, number | 'mixed'>; allocationAssignments?: Array<{teacher: string; employeeId: string | null; subject: string; classIds: number[]}>}} params
 */
export const getDataUploadDraftTemplate = (draftId, params, onSuccess, onFailure) => {
  const query = new URLSearchParams({format: 'json'})
  if (params?.flavour) query.set('flavour', params.flavour)
  if (params?.curriculumColumn) query.set('curriculum_column', 'true')
  if (params?.branchAssignments && Object.keys(params.branchAssignments).length > 0) {
    query.set('branchAssignments', JSON.stringify(params.branchAssignments))
  }
  // "Which classes?" answers: the template then carries the picked classes as allocation lines.
  if (Array.isArray(params?.allocationAssignments) && params.allocationAssignments.length > 0) {
    query.set('allocationAssignments', JSON.stringify(params.allocationAssignments))
  }
  return {
    type: DATA_UPLOAD_GET_DRAFT_TEMPLATE,
    payload: {
      apiUrl: constants.api_data_upload_draft_template(draftId),
      getString: `?${query.toString()}`,
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `GET data-upload/subject-aliases`: `result.aliases`, everything this school has confirmed its
 * subjects are also called ("Maths" -> "Mathematics"). Applied silently on every later upload.
 */
export const getDataUploadSubjectAliases = (onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_GET_SUBJECT_ALIASES,
    payload: {
      apiUrl: constants.api_data_upload_subject_aliases,
      getString: '',
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `POST data-upload/subject-aliases`: confirm mappings, or `forget` ones the user wants back.
 * Answers `{saved, removed, total, rejected}`; `rejected` lists anything mapped onto a subject
 * the school does not have (`reason: 'NOT_A_SCHOOL_SUBJECT'`).
 *
 * A confirmed mapping is not applied to a draft that was read before it existed — re-run the
 * extraction (post the same files again) for the rows to resolve with it.
 *
 * @param {{mappings?: Array<{written: string; subject: string; confidence?: number}>; forget?: string[]}} body
 */
export const putDataUploadSubjectAliases = (body, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_PUT_SUBJECT_ALIASES,
    payload: {
      apiUrl: constants.api_data_upload_subject_aliases,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      reduxActionType: '',
      body,
      onSuccess,
      onFailure,
    },
  }
}

/**
 * `GET data-upload/drafts/:id`: the whole draft, with `files[]` (one summary per uploaded file,
 * its `counts.records` being the rows that file contributed) and `records[]`.
 */
export const getDataUploadDraft = (draftId, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_GET_DRAFT,
    payload: {
      apiUrl: constants.api_data_upload_draft(draftId),
      getString: '',
      useBaseURL: true,
      requestType: 'GET',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}

/** `DELETE data-upload/drafts/:id`. */
export const deleteDataUploadDraft = (draftId, onSuccess, onFailure) => {
  return {
    type: DATA_UPLOAD_DELETE_DRAFT,
    payload: {
      apiUrl: constants.api_data_upload_draft(draftId),
      getString: '',
      useBaseURL: true,
      requestType: 'DELETE',
      header: 'application/json',
      reduxActionType: '',
      body: null,
      onSuccess,
      onFailure,
    },
  }
}
